<template>
  <component
    :is="component"
    :class="['stroke-m', 'neutral-50', 'outlined']"
    :to="to"
    class="button-library"
    v-bind="props"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
    @focus="handleMouseOver"
    @blur="handleMouseLeave"
    @click="handleClick"
  >
    <span v-if="mediaObject && mediaObject.url" class="icon">
      <span class="icon-wrapper">
        <span class="solid" :style="styleSolid" />
        <ImgLoad
          class="img"
          :alt="title"
          :src="mediaObject.url"
          effect="fade-in"
          :style="styleImg"
        />
      </span>
    </span>
    <span class="content">
      <h4>{{ title }}</h4>
      <p>{{ subtitle }}</p>
    </span>
  </component>
</template>

<script lang="ts" setup>
interface Props {
  title?: string;
  subtitle?: string;
  description?: string;
  colorCode?: string;
  icon?: string;
  metaColorCode?: string;
  metaContent?: string;
  metaIcon?: string;
  metaShortTitle?: string;
  metaSubtitle?: string;
  metaTitle?: string;
  to: string;
}
const props = defineProps({
  title: null,
  subtitle: null,
  description: null,
  colorCode: null,
  icon: null,
  metaColorCode: null,
  metaContent: null,
  metaIcon: null,
  metaShortTitle: null,
  metaSubtitle: null,
  metaTitle: null,
  to: null
});

const {
  libraryManager: { media },
  libraryStrategy,
} = inject(INJECT_LIBRARY_PAGE_OR_FORM);

const { scale: scaleSolid, style: styleSolid } = useScaleEffect(
  1,
  false,
  2,
  25,
  0.4
);
const { scale: scaleImg, style: styleImg } = useScaleEffect(
  1,
  false,
  1,
  20,
  0.1
);

const color = computed(() => props.colorCode || props.metaColorCode);
const title = computed(() => props.title || props.metaTitle);
const subtitle = computed(() => props.subtitle || props.metaSubtitle);
const mediaId = computed(() => props.icon || props.metaIcon);
const mediaObject = computed(() => media.value.get(mediaId.value));

const component = computed(() => {
  if (libraryStrategy.type === 'page') {
    return resolveComponent('NuxtLink');
  }
  return 'button';
});

const handleMouseOver = () => {
  scaleSolid.value = 1.1;
  scaleImg.value = 1.3;
};
const handleMouseLeave = () => {
  scaleSolid.value = 1;
  scaleImg.value = 1;
};

const handleClick = () => {
  if (libraryStrategy.type === 'form') {
    libraryStrategy.setRoute(props.to);
  }
};
</script>
<style lang="scss" scoped>
.button-library {
  @include font('s', 500);

  display: grid;
  grid-template-columns: 30% 70%;
  align-items: center;
  overflow: hidden;
  transition: box-shadow 300ms $ease-out-quad, border 500ms $ease-in-quad,
    background-color 200ms $ease-in-quad;

  @mixin hover-focus-desktop {
    transition: box-shadow 200ms $ease-in-quad, border 500ms $ease-in-quad,
      background-color 200ms $ease-in-quad;
    border: var(--stroke_thickness_n) solid var(--color_neutral_100);
    background-color: var(--color_neutral_25);
    box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.4rem;
  .icon-wrapper {
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 1;
  }
  .solid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: v-bind(color);
    aspect-ratio: 1;
  }
  .img {
    width: 76%;
    object-fit: contain;
    aspect-ratio: 1;
  }
}
.content {
  display: flex;
  position: relative;
  z-index: 1;
  flex-direction: column;
  justify-content: center;
  padding: 1.4rem 1rem 1.4rem 0.4rem;
  gap: var(--gap_xxs);
  p {
    color: var(--color_neutral_600);
  }
}

h4 {
  color: var(--color_neutral_700);
  @include font('m', 800, 'secondary');
}
</style>
